import React, { useState, useEffect, useRef } from 'react';
import {
  useTranslate,
  SimpleShowLayout,
  useNotify,
  useRedirect,
} from 'react-admin';
import {
  Divider,
  TextField,
  Button,
  CircularProgress,
  IconButton,
} from '@material-ui/core';
import { ExpandMore, ExpandLess } from '@material-ui/icons';
import SectionTitle from '../SectionTitle';
import CustomSelect from '../CustomSelect';
import CustomTextArea from '../CustomTextArea';
import { uploadFile } from '../../utils/requests';

import './styles.scss';

interface FirstTimeIntakeFormProps {
  readonly: boolean;
  data: any;
  stepId: string;
}

const getYesNoValue = (value: boolean | null | undefined): string => {
  if (value === null || value === undefined) {
    return '';
  }
  return value ? 'true' : 'false';
};

const FirstTimeIntakeForm = ({
  readonly,
  data,
  stepId,
}: FirstTimeIntakeFormProps) => {
  const translate = useTranslate();
  const notify = useNotify();
  const redirect = useRedirect();
  const stepData = data.steps.find((step: any) => step.id === stepId);

  // State for selected date with default value of today
  const [selectedDate, setSelectedDate] = useState<string>(
    stepData?.consultationData?.firstTimeIntakeDate
      ? new Date(stepData?.consultationData?.firstTimeIntakeDate)
          .toISOString()
          .split('T')[0]
      : new Date().toISOString().split('T')[0],
  );

  // State for first time intake eligibility
  const [firstTimeIntakeWithoutIssues, setFirstTimeIntakeWithoutIssues] =
    useState<string>(
      getYesNoValue(stepData?.consultationData?.firstTimeIntakeWithoutIssues),
    );

  const [medicationForThreeMonthsIssued, setMedicationForThreeMonthsIssued] =
    useState<string>(
      getYesNoValue(stepData?.consultationData?.medicationForThreeMonthsIssued),
    );
  const [note, setNote] = useState<string>(
    stepData?.consultationData?.note || '',
  );

  // State for file upload
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFileName, setSelectedFileName] = useState<string | null>(null);

  // State for form submission
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [formErrors, setFormErrors] = useState<string[]>([]);

  // State for accordion expand/collapse
  const [isExpanded, setIsExpanded] = useState<boolean>(!readonly);

  // Clear errors whenever form state changes
  useEffect(() => {
    if (formErrors.length > 0) {
      setFormErrors([]);
    }
  }, [
    selectedDate,
    firstTimeIntakeWithoutIssues,
    medicationForThreeMonthsIssued,
    note,
    selectedFileName,
  ]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = event.target;
    if (files && files.length > 0) {
      const file = files[0];
      // Validate PDF file type
      if (file.type !== 'application/pdf') {
        notify('File must be a PDF', { type: 'warning' });
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        setSelectedFileName(null);
        return;
      }
      setSelectedFileName(file.name);
    }
  };

  const handleDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedDate(event.target.value);
  };

  const handleFirstTimeIntakeWithoutIssuesChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    setFirstTimeIntakeWithoutIssues(event.target.value);
  };

  const handlemedicationForThreeMonthsIssuedChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    setMedicationForThreeMonthsIssued(event.target.value);
  };

  const handleNoteChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNote(event.target.value);
  };

  const validateForm = (): string[] => {
    const errors: string[] = [];

    // Validate date is selected
    if (!selectedDate) {
      errors.push(
        `${translate(
          'immunotherapy.firstTimeIntake.selectDateTitle',
        )} ${translate('ra.validation.required')}`,
      );
    }

    // Validate both yes/no questions are answered
    if (!firstTimeIntakeWithoutIssues) {
      errors.push(
        `${translate(
          'immunotherapy.firstTimeIntake.questionForWithoutIssues',
        )} ${translate('ra.validation.required')}`,
      );
    }

    if (!medicationForThreeMonthsIssued) {
      errors.push(
        `${translate(
          'immunotherapy.firstTimeIntake.medicationForThreeMonthsIssued',
        )} ${translate('ra.validation.required')}`,
      );
    }

    // Validate note is required if either answer is "no" (value is 'false')
    if (
      (firstTimeIntakeWithoutIssues === 'false' ||
        medicationForThreeMonthsIssued === 'false') &&
      !note.trim()
    ) {
      errors.push(
        `${translate('immunotherapy.preparatoryCall.noteLabel')} ${translate(
          'ra.validation.required',
        )}`,
      );
    }

    // Validate prescription file is selected
    if (!selectedFileName || !fileInputRef.current?.files?.length) {
      errors.push(
        translate('immunotherapy.firstTimeIntake.prescriptionFileRequired'),
      );
    }

    return errors;
  };

  const handleSubmit = async () => {
    const errors = validateForm();
    setFormErrors(errors);

    if (errors.length > 0) {
      return;
    }

    try {
      setIsLoading(true);

      // Ensure date is in YYYY-MM-DD format
      const formattedDate = selectedDate.includes('T')
        ? selectedDate.split('T')[0]
        : selectedDate;

      // Get the selected file
      if (
        !fileInputRef.current?.files ||
        fileInputRef.current.files.length === 0
      ) {
        throw new Error('No file selected');
      }

      const selectedFile = fileInputRef.current.files[0];

      // Prepare form data to send with file upload
      // Convert all values to strings as FormData requires string values
      const formData: Record<string, string> = {
        activeStepId: String(data.activeStepId || ''),
        firstTimeIntakeDate: formattedDate,
        firstTimeIntakeWithoutIssues: String(
          firstTimeIntakeWithoutIssues === 'true',
        ),
        medicationForThreeMonthsIssued: String(
          medicationForThreeMonthsIssued === 'true',
        ),
        note: note || '',
      };

      // Upload file with form data to the backend endpoint
      await uploadFile(
        `treatments/${data.id}/upload-file`,
        selectedFile,
        formData,
      );

      notify('tests.addReportSuccessMessage', { type: 'success' });
      redirect('/treatments');
    } catch (e) {
      notify('tests.addReportErrorMessage', { type: 'warning' });
    } finally {
      setIsLoading(false);
    }
  };

  const medicationName = stepData?.medication
    ? stepData.medication.charAt(0).toUpperCase() + stepData.medication.slice(1)
    : stepData?.medication;

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleExpanded();
    }
  };

  return (
    <SimpleShowLayout>
      <Divider className="divider" />
      <div className="formWrapper">
        <div
          className="accordion-header"
          onClick={toggleExpanded}
          onKeyDown={handleKeyDown}
          role="button"
          tabIndex={0}
          aria-expanded={isExpanded}
        >
          <SectionTitle
            title={`${translate('immunotherapy.firstTimeIntake.title')} - ${
              medicationName || ''
            }`}
          />
          <IconButton
            className="accordion-toggle-icon"
            size="small"
            aria-label="expand or collapse"
          >
            {isExpanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </div>
        {isExpanded && (
          <>
            <div className="treatmentBlock treatmentBlock-list">
              <div className="treatmentBlock-subtitle">
                {translate('immunotherapy.firstTimeIntake.instructionsTitle')}
              </div>
              <div className="treatmentBlock-text firstTimeIntake-text">
                1. {translate('immunotherapy.firstTimeIntake.firstInstruction')}
              </div>
              <div className="treatmentBlock-text firstTimeIntake-text">
                2.{' '}
                {translate('immunotherapy.firstTimeIntake.secondInstruction')}
              </div>
              <div className="treatmentBlock-text firstTimeIntake-text">
                3. {translate('immunotherapy.firstTimeIntake.thirdInstruction')}
              </div>
              <div className="treatmentBlock-text firstTimeIntake-text">
                4.{' '}
                {translate('immunotherapy.firstTimeIntake.fourthInstruction')}
              </div>
              <div className="treatmentBlock-text firstTimeIntake-text">
                5. {translate('immunotherapy.firstTimeIntake.fifthInstruction')}
              </div>
            </div>
            <div className="treatmentBlock treatmentBlock-list">
              <div className="treatmentBlock-subtitle">
                {translate('immunotherapy.firstTimeIntake.selectDateTitle')}
              </div>
              <TextField
                type="date"
                value={selectedDate}
                onChange={handleDateChange}
                disabled={readonly}
                style={{ width: '180px' }}
                InputLabelProps={{
                  shrink: true,
                }}
                className="firstTimeIntake-datepicker"
              />
            </div>
            <div className="treatmentBlock">
              <div className="firstTimeIntake-question-container">
                <div className="treatmentBlock-subtitle">
                  {translate(
                    'immunotherapy.firstTimeIntake.questionForWithoutIssues',
                  )}
                </div>
                <div className="treatmentBlock-text">
                  {translate(
                    'immunotherapy.firstTimeIntake.questionForWithoutIssuesExplanation',
                  )}
                </div>
              </div>
              <CustomSelect
                emptyLabel={translate(
                  'immunotherapy.treatment.selectEligibility',
                )}
                options={[
                  {
                    value: 'true',
                    label: translate('immunotherapy.treatment.yes'),
                  },
                  {
                    value: 'false',
                    label: translate('immunotherapy.treatment.no'),
                  },
                ]}
                value={firstTimeIntakeWithoutIssues}
                onChange={handleFirstTimeIntakeWithoutIssuesChange}
                disabled={readonly}
                error={!firstTimeIntakeWithoutIssues}
              />
            </div>
            <div className="treatmentBlock">
              <div className="firstTimeIntake-question-container">
                <div className="treatmentBlock-subtitle">
                  {translate(
                    'immunotherapy.firstTimeIntake.medicationForThreeMonthsIssued',
                  )}
                </div>
                <div className="treatmentBlock-text">
                  {translate(
                    'immunotherapy.firstTimeIntake.medicationForThreeMonthsIssuedExplanation',
                  )}
                </div>
              </div>
              <CustomSelect
                emptyLabel={translate(
                  'immunotherapy.treatment.selectEligibility',
                )}
                options={[
                  {
                    value: 'true',
                    label: translate('immunotherapy.treatment.yes'),
                  },
                  {
                    value: 'false',
                    label: translate('immunotherapy.treatment.no'),
                  },
                ]}
                value={medicationForThreeMonthsIssued}
                onChange={handlemedicationForThreeMonthsIssuedChange}
                disabled={readonly}
                error={!medicationForThreeMonthsIssued}
              />
            </div>
            <div className="treatmentBlock treatmentBlock-list">
              <div className="treatmentBlock-subtitle">
                {translate('immunotherapy.preparatoryCall.noteLabel')}
              </div>
              <CustomTextArea
                value={note}
                onChange={handleNoteChange}
                disabled={readonly}
              />
            </div>
            {!readonly && (
              <div className="treatmentBlock">
                <div className="treatmentBlock-subtitle">
                  {translate('immunotherapy.firstTimeIntake.uploadButtonLabel')}
                </div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                    marginLeft: 'auto',
                  }}
                >
                  {selectedFileName && (
                    <div
                      style={{
                        color: '#666',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      {selectedFileName}
                    </div>
                  )}
                  <input
                    accept="application/pdf"
                    style={{ display: 'none' }}
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    component="span"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isLoading}
                  >
                    {translate(
                      'immunotherapy.firstTimeIntake.uploadButtonText',
                    )}
                  </Button>
                </div>
              </div>
            )}
            {formErrors.length > 0 && (
              <div className="formErrors">
                <ul>
                  {formErrors.map((error) => (
                    <li key={error} className="error">
                      * {error}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {!readonly && (
              <div className="treatmentBlock">
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSubmit}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <CircularProgress size={24.5} />
                  ) : (
                    translate('immunotherapy.preparatoryCall.submitButtonText')
                  )}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </SimpleShowLayout>
  );
};

export default FirstTimeIntakeForm;
